import type { MicErrorKind } from '@/lib/mic/useMicController';
import './MicOverlay.css';

/**
 * Shown when the mic can't be used — denied, blocked at the OS level,
 * unsupported, or something we don't recognize. Always offers a path to
 * keep going (text fallback) so the session doesn't dead-end.
 */

interface MicErrorRecoveryProps {
  open: boolean;
  errorKind: MicErrorKind | null;
  onRetry: () => void;
  onDismiss: () => void;
}

export default function MicErrorRecovery({
  open,
  errorKind,
  onRetry,
  onDismiss,
}: MicErrorRecoveryProps) {
  if (!open || !errorKind) return null;

  const copy = COPY[errorKind] ?? COPY.unknown;

  return (
    <div className="mic-overlay" role="dialog" aria-modal="true" aria-labelledby="mic-error-title">
      <div className="mic-overlay__backdrop" onClick={onDismiss} />
      <div className="mic-overlay__card mic-overlay__card--alert">
        <p className="mic-overlay__eyebrow mic-overlay__eyebrow--danger">Microphone unavailable</p>
        <h2 id="mic-error-title" className="mic-overlay__title">
          {copy.title}
        </h2>
        <p className="mic-overlay__body">{copy.body}</p>
        {copy.steps.length > 0 && (
          <ol className="mic-overlay__steps">
            {copy.steps.map((s, i) => (
              <li key={i}>{s}</li>
            ))}
          </ol>
        )}
        <div className="mic-overlay__actions">
          <button type="button" className="mic-overlay__btn-ghost" onClick={onDismiss}>
            Use text instead
          </button>
          <button type="button" className="mic-overlay__btn-primary" onClick={onRetry}>
            Try again
          </button>
        </div>
      </div>
    </div>
  );
}

const COPY: Record<MicErrorKind, { title: string; body: string; steps: string[] }> = {
  denied: {
    title: 'The browser blocked microphone access.',
    body: 'You can grant access in your browser settings, then come back and tap the mic again. Until then, type your responses — every feature still works.',
    steps: [
      "Click the lock or camera icon in your browser's address bar.",
      'Set "Microphone" to Allow for this site.',
      'Reload the page if needed, then tap the mic.',
    ],
  },
  unavailable: {
    title: 'No microphone is reachable.',
    body: "Your browser couldn't find a working microphone. Check that your input device is plugged in and selected as the system default.",
    steps: [
      'Plug in a wired or Bluetooth microphone.',
      'Open your OS sound settings and pick that mic as the input.',
      'Tap "Try again" once it\'s connected.',
    ],
  },
  unsupported: {
    title: "This browser doesn't expose the microphone.",
    body: 'Voice input needs a modern browser — try Chrome, Edge, Safari, or Firefox. You can keep going with text in the meantime.',
    steps: [],
  },
  unknown: {
    title: 'Something went wrong starting the mic.',
    body: 'Tap "Try again" to reattempt. If it keeps failing, refresh the page or use text input.',
    steps: [],
  },
};
